import React, { useState, useEffect } from 'react';
import { X, ArrowRightCircle, Calendar, User, Building2, Filter, Eye, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TransferHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Transfer {
  id: string;
  conversationId: string;
  protocolId: string;
  customerName: string | null;
  customerPhone: string;
  fromAgentId: string | null;
  toAgentId: string | null;
  fromDepartmentId: string | null;
  toDepartmentId: string;
  transferType: string;
  transferredAt: string;
  conversation: {
    status: string;
  };
  fromAgent: {
    id: string;
    name: string;
    email: string;
  } | null;
  toAgent: {
    id: string;
    name: string;
    email: string;
  } | null;
  fromDepartment: {
    id: string;
    name: string;
  } | null;
  toDepartment: {
    id: string;
    name: string;
  };
}

interface Agent {
  id: string;
  name: string;
  email: string;
}

const formatPhoneNumber = (phone: string) => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format Brazilian phone numbers
  if (cleaned.length === 13 && cleaned.startsWith('55')) {
    // +55 11 99999-9999
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  } else if (cleaned.length === 11) {
    // 11 99999-9999
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }

  // Return original if doesn't match expected format
  return phone;
};

const getStatusBadge = (status: string) => {
  const statusConfig: Record<string, { label: string; colorClass: string }> = {
    WAITING: { label: 'Aguardando', colorClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
    MENU: { label: 'Menu', colorClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
    QUEUED: { label: 'Na Fila', colorClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
    ACTIVE: { label: 'Ativo', colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
    CLOSED: { label: 'Fechado', colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
    AVALIACAO: { label: 'Avaliação', colorClass: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
    ANSWERED: { label: 'Respondido', colorClass: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  };

  const config = statusConfig[status] || { label: status, colorClass: 'bg-gray-100 text-gray-800' };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${config.colorClass}`}>
      {config.label}
    </span>
  );
};

export const TransferHistoryModal: React.FC<TransferHistoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingConversationId, setLoadingConversationId] = useState<string | null>(null);

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedFromAgentId, setSelectedFromAgentId] = useState('');
  const [selectedToAgentId, setSelectedToAgentId] = useState('');
  const [selectedTransferType, setSelectedTransferType] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAgents();
      loadTransfers();
    }
  }, [isOpen]);

  const loadAgents = async () => {
    try {
      const data = await api.getUsers({});
      setAgents(data.filter(agent => agent.role !== 'ADMIN'));
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadTransfers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedFromAgentId) params.fromAgentId = selectedFromAgentId;
      if (selectedToAgentId) params.toAgentId = selectedToAgentId;
      if (selectedTransferType) params.transferType = selectedTransferType;

      // Use a generic request since we haven't added to api.ts yet
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/conversations/transfers/history?${new URLSearchParams(params)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch transfer history');
      }

      const data = await response.json();
      setTransfers(data.transfers);
    } catch (error: any) {
      console.error('Failed to load transfer history:', error);
      setError(error.message || 'Falha ao carregar histórico de transferências');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterApply = () => {
    loadTransfers();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedFromAgentId('');
    setSelectedToAgentId('');
    setSelectedTransferType('');
  };

  const handleViewConversation = (conversationId: string) => {
    setLoadingConversationId(conversationId);
    // Small delay to show loading state before navigation
    setTimeout(() => {
      navigate(`/conversations/${conversationId}?status=all`);
      onClose(); // Close modal after navigation
    }, 150);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-6xl z-50 max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ArrowRightCircle className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Histórico de Transferências
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Visualize todas as transferências de atendimentos
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtros</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data Início
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data Fim
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* From Agent */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  De (Agente)
                </label>
                <select
                  value={selectedFromAgentId}
                  onChange={(e) => setSelectedFromAgentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* To Agent */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Para (Agente)
                </label>
                <select
                  value={selectedToAgentId}
                  onChange={(e) => setSelectedToAgentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Transfer Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tipo
                </label>
                <select
                  value={selectedTransferType}
                  onChange={(e) => setSelectedTransferType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Todos</option>
                  <option value="MANUAL">Manual</option>
                  <option value="AUTOMATIC">Automático</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleFilterApply}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white text-sm rounded-lg transition"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-sm rounded-lg transition"
              >
                Limpar Filtros
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : error ? (
              <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
                {error}
              </div>
            ) : transfers.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <ArrowRightCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Nenhuma transferência encontrada com os filtros selecionados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Protocolo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Telefone
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        De
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">

                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Para
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Data/Hora
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ação
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {transfers.map((transfer) => (
                      <tr key={transfer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        {/* Protocolo */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full text-xs font-mono font-semibold">
                            #{transfer.protocolId}
                          </span>
                        </td>

                        {/* Cliente */}
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {transfer.customerName || 'Sem nome'}
                          </span>
                        </td>

                        {/* Telefone */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                            {formatPhoneNumber(transfer.customerPhone)}
                          </span>
                        </td>

                        {/* De */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 text-sm">
                            {transfer.fromDepartment && (
                              <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                                <Building2 className="w-4 h-4" />
                                <span>{transfer.fromDepartment.name}</span>
                              </div>
                            )}
                            {transfer.fromAgent && (
                              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                <User className="w-4 h-4" />
                                <span>{transfer.fromAgent.name}</span>
                              </div>
                            )}
                            {!transfer.fromDepartment && !transfer.fromAgent && (
                              <span className="text-gray-400 italic text-xs">Menu</span>
                            )}
                          </div>
                        </td>

                        {/* Arrow */}
                        <td className="px-4 py-3 text-center">
                          <ArrowRightCircle className="w-5 h-5 text-purple-500 mx-auto" />
                        </td>

                        {/* Para */}
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 text-sm">
                            <div className="flex items-center gap-1 text-gray-700 dark:text-gray-300">
                              <Building2 className="w-4 h-4" />
                              <span>{transfer.toDepartment.name}</span>
                            </div>
                            {transfer.toAgent ? (
                              <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                <User className="w-4 h-4" />
                                <span>{transfer.toAgent.name}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic text-xs">(Fila)</span>
                            )}
                          </div>
                        </td>

                        {/* Tipo */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {transfer.transferType === 'AUTOMATIC' ? (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">
                              Automático
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">
                              Manual
                            </span>
                          )}
                        </td>

                        {/* Status Atual */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          {getStatusBadge(transfer.conversation.status)}
                        </td>

                        {/* Data/Hora */}
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                            <Calendar className="w-3 h-3" />
                            {format(parseISO(transfer.transferredAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </div>
                        </td>

                        {/* Ação - Ver Conversa */}
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleViewConversation(transfer.conversationId)}
                            disabled={loadingConversationId === transfer.conversationId}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white text-xs rounded transition disabled:opacity-70 disabled:cursor-not-allowed"
                            title="Ver conversa"
                          >
                            {loadingConversationId === transfer.conversationId ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Abrindo...
                              </>
                            ) : (
                              <>
                                <Eye className="w-3.5 h-3.5" />
                                Ver
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total: {transfers.length} transferência{transfers.length !== 1 ? 's' : ''}
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-600 dark:hover:bg-gray-500 text-white rounded-lg transition"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
